import { Injectable, inject } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { gql } from 'apollo-angular';
import { Observable, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GraphqlResponseStatus } from 'src/app/core/graph-api/services/graph-api/models/graphql-response-status';
import { GraphApiService } from '../../../core/graph-api/services/graph-api/graph-api.service';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';
import { FeedbackCategory } from '../feedback-modal/models/feedback-category.enum';

const CREATE_FEEDBACK_MUTATION = gql`
  mutation CreateFeedback($command: FeedbackMessageCommandInput!) {
    feedback {
      createFeedback(command: $command)
    }
  }
`;

export interface IFeedbackData {
  category: FeedbackCategory;
  title: string;
  description: string;
  path: string;
  organizationId: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackModalService {
  private modalService = inject(NgbModal);
  private graphApiService = inject(GraphApiService);

  /**
   * Opens the feedback modal
   * @returns Observable that emits the feedback data when submitted, or null if cancelled
   */
  public openFeedbackModal(): Observable<IFeedbackData | null> {
    const modalRef: NgbModalRef = this.modalService.open(FeedbackModalComponent, { size: 'lg' });

    return from(modalRef.result).pipe(
      catchError(() => [null]), // Return null if modal is dismissed
    );
  }

  /**
   * Submits feedback data via GraphQL mutation
   * @param feedbackData The feedback data to submit
   * @returns Observable that emits the created feedback response
   */
  public submitFeedback(command: IFeedbackData): Observable<boolean> {
    return this.graphApiService
      .mutateV2<{ feedback: { createFeedback: string } }>({
        mutation: CREATE_FEEDBACK_MUTATION,
        variables: { command },
      })
      .pipe(map((result) => (result?.feedback.createFeedback === GraphqlResponseStatus.Ok ? true : false)));
  }
}
